import { createTheme } from '@mui/material/styles';
import { tokens } from './tokens';

declare module '@mui/material/styles' {
  interface TypographyVariants {
    brand: React.CSSProperties;
    navigation: React.CSSProperties;
    tagline: React.CSSProperties;
    numeric: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    brand?: React.CSSProperties;
    navigation?: React.CSSProperties;
    tagline?: React.CSSProperties;
    numeric?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    brand: true;
    navigation: true;
    tagline: true;
    numeric: true;
  }
}

const display = {
  fontFamily: tokens.display,
  fontWeight: 700,
  letterSpacing: '-0.02em',
  color: tokens.ink,
} as const;

const theme = createTheme({
  palette: {
    primary: {
      main: tokens.accent,
      dark: tokens.accentDark,
      light: '#3B9C94',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: tokens.accentDeep,
      contrastText: '#FFFFFF',
    },
    error: { main: tokens.danger },
    warning: { main: tokens.warn },
    success: { main: tokens.accent },
    info: { main: '#1D4ED8' },
    background: {
      default: tokens.bg,
      paper: tokens.surface,
    },
    text: {
      primary: tokens.ink,
      secondary: tokens.ink2,
    },
    divider: tokens.line,
  },
  typography: {
    fontFamily: tokens.body,
    // Page titles: the greeting, "Lançamentos", "Planejamento".
    brand: { ...display, fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.025em' },
    navigation: { fontFamily: tokens.body, fontWeight: 600, fontSize: '0.95rem' },
    tagline: { fontFamily: tokens.body, fontWeight: 500, fontSize: '0.8rem', color: tokens.ink3 },
    numeric: { fontFamily: tokens.display, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
    h1: { ...display, fontSize: '2.25rem' },
    h2: { ...display, fontSize: '1.875rem' },
    h3: { ...display, fontSize: '1.5rem' },
    h4: { ...display, fontSize: '1.75rem', letterSpacing: '-0.025em' },
    h5: { ...display, fontSize: '1.25rem', fontWeight: 650 },
    h6: { ...display, fontSize: '1.0625rem', fontWeight: 650, letterSpacing: '-0.01em' },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: {
    borderRadius: tokens.radius.control,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bg,
          WebkitFontSmoothing: 'antialiased',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.pill,
          textTransform: 'none',
          fontWeight: 600,
          paddingLeft: 18,
          paddingRight: 18,
          minHeight: 40,
        },
        sizeSmall: { minHeight: 34, paddingLeft: 14, paddingRight: 14 },
        sizeLarge: { minHeight: 48, paddingLeft: 24, paddingRight: 24 },
        outlined: {
          borderColor: tokens.line,
          color: tokens.ink,
          '&:hover': { borderColor: tokens.ink3, backgroundColor: tokens.bg },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.card,
          border: `1px solid ${tokens.line}`,
          backgroundImage: 'none',
          boxShadow: 'none',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: { root: { padding: 22, '&:last-child': { paddingBottom: 22 } } },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        rounded: { borderRadius: 16 },
        outlined: { borderColor: tokens.line },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          border: `1px solid ${tokens.line}`,
          boxShadow: tokens.shadow.menu,
          marginTop: 6,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: { root: { minHeight: 44, borderRadius: 10, margin: '0 6px' } },
    },
    MuiPopover: {
      styleOverrides: { paper: { borderRadius: 14, boxShadow: tokens.shadow.menu } },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 24, boxShadow: tokens.shadow.menu },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontFamily: tokens.display, fontWeight: 700, fontSize: '1.3rem', letterSpacing: '-0.015em', padding: '22px 24px 8px' },
      },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: '8px 24px 12px' } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '12px 24px 22px', gap: 8 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.control,
          backgroundColor: tokens.surface,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: tokens.line },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: tokens.ink3 },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: tokens.radius.pill, fontWeight: 600 },
        outlined: { borderColor: tokens.line },
      },
    },
    MuiToggleButton: {
      styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
    },
    MuiTab: {
      styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'inherit' },
      styleOverrides: {
        root: { backgroundColor: tokens.surface, borderBottom: `1px solid ${tokens.line}` },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 99, backgroundColor: tokens.line },
        bar: { borderRadius: 99 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: tokens.line },
        head: { fontWeight: 600, color: tokens.ink2 },
      },
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { backgroundColor: tokens.ink, fontSize: 12.5, borderRadius: 8 } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 14 } },
    },
    MuiListItemButton: {
      styleOverrides: { root: { borderRadius: 12 } },
    },
    MuiFab: {
      styleOverrides: { root: { boxShadow: tokens.shadow.soft } },
    },
    MuiAvatar: {
      styleOverrides: { root: { fontWeight: 700 } },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { fontWeight: 600 } },
    },
  },
});

export default theme;
